import React, { useState, useEffect, useMemo } from "react";
import { useParams, useSearchParams, Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { get } from "../lib/api";
import { Seo, breadcrumbJsonLd, faqJsonLd } from "../lib/seo";
import { Section, Breadcrumb, Button, Spinner } from "../components/ui";
import { Markdown } from "../components/markdown";
import { FaqAccordion } from "../components/marketing";
import { CALC, NICHE } from "../data/calculators";

const peso = (n) => "\u20b1" + Math.round(n).toLocaleString("en-PH");

function SchemaCalculator({ schema }) {
  const [sp, setSp] = useSearchParams();
  const init = useMemo(() => {
    const o = {}; schema.inputs.forEach((i) => { o[i.k] = sp.get(i.k) != null ? Number(sp.get(i.k)) : i.def; }); return o;
  }, [schema]);
  const [v, setV] = useState(init);
  const [copied, setCopied] = useState(false);
  useEffect(() => { const n = new URLSearchParams(); Object.entries(v).forEach(([k, val]) => n.set(k, val)); setSp(n, { replace: true }); }, [v]);
  const outputs = schema.compute(v);
  const copy = () => { navigator.clipboard.writeText(window.location.href); setCopied(true); setTimeout(() => setCopied(false), 1800); };
  return (
    <div className="grid gap-8 lg:grid-cols-2 items-start">
      <div className="card-hairline p-6 space-y-5">
        <div className="eyebrow">Your inputs</div>
        {schema.inputs.map((i) => (
          <label key={i.k} className="block">
            <span className="flex justify-between text-sm text-ink-600"><span>{i.label}</span><span className="font-mono text-ink-900">{v[i.k]}</span></span>
            <input type="range" min={i.min} max={i.max} step={i.step} value={v[i.k]} onChange={(e) => setV((s) => ({ ...s, [i.k]: Number(e.target.value) }))} className="w-full mt-2 accent-brand-600" />
          </label>
        ))}
      </div>
      <div className="space-y-4" aria-live="polite">
        <div className="card-hairline p-6">
          <div className="eyebrow mb-4">Result</div>
          <dl className="space-y-3">
            {outputs.map(([k, val], i) => (
              <div key={i} className={`flex justify-between ${i < outputs.length - 1 ? "border-b border-line pb-2" : ""}`}>
                <dt className="text-ink-600">{k}</dt><dd className={`font-mono ${i === outputs.length - 1 ? "text-brand-700 text-lg" : "text-ink-950"}`}>{val}</dd>
              </div>
            ))}
          </dl>
          <button onClick={copy} className="btn-secondary btn-md mt-5 w-full">{copied ? "Link copied" : "Copy shareable result"}</button>
        </div>
        <div className="card-hairline p-5">
          <div className="eyebrow mb-2">How this is calculated</div>
          <p className="font-mono text-xs text-ink-800 break-words">{schema.formula}</p>
          {schema.assumptions?.length > 0 && <ul className="mt-3 space-y-1">{schema.assumptions.map((a, i) => <li key={i} className="text-xs text-ink-500">• {a}</li>)}</ul>}
        </div>
        <Button to="/pricing/" variant="primary" size="lg" className="w-full">See which plan fits</Button>
      </div>
    </div>
  );
}

function NicheCalculator({ cfg }) {
  const [vals, setVals] = useState([cfg.defs[0], ...cfg.defs.slice(1)]);
  const [deal, setDeal] = useState(cfg.deal);
  const counts = useMemo(() => {
    const out = [vals[0]];
    for (let i = 1; i < cfg.stages.length; i++) out.push(out[i - 1] * (vals[i] / 100));
    return out;
  }, [vals, cfg]);
  const revenue = counts[counts.length - 1] * deal;
  return (
    <div className="grid gap-8 lg:grid-cols-2 items-start">
      <div className="card-hairline p-6 space-y-5">
        <div className="eyebrow">Your funnel</div>
        <label className="block"><span className="flex justify-between text-sm text-ink-600"><span>{cfg.stages[0]} / mo</span><span className="font-mono text-ink-900">{vals[0]}</span></span>
          <input type="range" min={0} max={5000} step={10} value={vals[0]} onChange={(e) => setVals((s) => [Number(e.target.value), ...s.slice(1)])} className="w-full mt-2 accent-brand-600" /></label>
        {cfg.stages.slice(1).map((st, i) => (
          <label key={st} className="block"><span className="flex justify-between text-sm text-ink-600"><span>{st} rate %</span><span className="font-mono text-ink-900">{vals[i + 1]}</span></span>
            <input type="range" min={0} max={100} step={1} value={vals[i + 1]} onChange={(e) => setVals((s) => { const n = [...s]; n[i + 1] = Number(e.target.value); return n; })} className="w-full mt-2 accent-brand-600" /></label>
        ))}
        <label className="block"><span className="flex justify-between text-sm text-ink-600"><span>{cfg.dealLabel} (\u20b1)</span><span className="font-mono text-ink-900">{deal}</span></span>
          <input type="range" min={0} max={5000000} step={1000} value={deal} onChange={(e) => setDeal(Number(e.target.value))} className="w-full mt-2 accent-brand-600" /></label>
      </div>
      <div className="space-y-4" aria-live="polite">
        <div className="card-hairline p-6">
          <div className="eyebrow mb-4">Result</div>
          <dl className="space-y-3">
            {cfg.stages.map((st, i) => (
              <div key={st} className="flex justify-between border-b border-line pb-2"><dt className="text-ink-600">{st} / mo</dt><dd className="font-mono text-ink-950">{Math.round(counts[i]).toLocaleString("en-PH")}</dd></div>
            ))}
            <div className="flex justify-between"><dt className="text-ink-800">Monthly revenue</dt><dd className="font-mono text-brand-700 text-lg">{peso(revenue)}</dd></div>
          </dl>
        </div>
        <div className="card-hairline p-5"><div className="eyebrow mb-2">How this is calculated</div>
          <p className="font-mono text-xs text-ink-800">Each stage = previous stage \u00d7 stage rate. Revenue = final stage \u00d7 {cfg.dealLabel.toLowerCase()}.</p>
          <p className="text-xs text-ink-500 mt-2">Defaults reflect typical figures for this vertical; adjust to your own.</p>
        </div>
        <Button to="/pricing/" variant="primary" size="lg" className="w-full">See which plan fits</Button>
      </div>
    </div>
  );
}

const PICKER_Q = [
  { k: "channels", q: "How many channels do you need?", opts: [["1", 0], ["2\u20133", 1], ["4+", 2]] },
  { k: "volume", q: "Monthly conversation volume?", opts: [["Under 50", 0], ["50\u2013200", 1], ["200\u2013500", 2], ["500+", 3]] },
  { k: "media", q: "What media do you receive?", opts: [["Text & voice", 0], ["+ photos", 1], ["+ video & docs", 2]] },
  { k: "api", q: "Need API, webhooks or BYOK?", opts: [["No", 0], ["Yes", 2]] },
  { k: "team", q: "Team size?", opts: [["1\u20133", 0], ["4\u20135", 1], ["6\u201310", 2], ["10+", 3]] },
];
function PlanPicker() {
  const [a, setA] = useState({});
  const nav = useNavigate();
  const score = Math.max(...Object.values({ x: 0, ...a }));
  const plan = score >= 3 ? "Enterprise" : score >= 2 ? "Pro" : score >= 1 ? "Growth" : "Starter";
  const done = Object.keys(a).length === PICKER_Q.length;
  const priceMap = { Starter: "\u20b11,495", Growth: "\u20b15,995", Pro: "\u20b111,995", Enterprise: "Custom" };
  return (
    <div className="grid gap-8 lg:grid-cols-2 items-start">
      <div className="card-hairline p-6 space-y-5">
        {PICKER_Q.map((q) => (
          <div key={q.k}>
            <div className="text-sm text-ink-800 mb-2">{q.q}</div>
            <div className="flex flex-wrap gap-2">
              {q.opts.map(([label, val]) => (
                <button key={label} onClick={() => setA((s) => ({ ...s, [q.k]: val }))} className={`badge ${a[q.k] === val ? "!bg-ink-950 !text-white !border-ink-950" : ""}`}>{label}</button>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="space-y-4">
        <div className="card-hairline p-6">
          <div className="eyebrow mb-2">Recommendation</div>
          {done ? (
            <>
              <div className="text-3xl text-ink-950">{plan}</div>
              <div className="font-mono text-brand-700 mt-1">{priceMap[plan]}{plan !== "Enterprise" && "/mo"}</div>
              <p className="text-sm text-ink-600 mt-3">Based on your answers, {plan} fits your channels, volume, media and team. The runner-up is {plan === "Starter" ? "Growth" : plan === "Enterprise" ? "Pro" : "the tier below"}.</p>
              <Button to={plan === "Enterprise" ? "/contact/?intent=sales" : "/pricing/"} variant="primary" size="md" className="mt-4 w-full" data-testid="tooldetail-cta-btn">{plan === "Enterprise" ? "Contact sales" : "See Pricing"}</Button>
            </>
          ) : <p className="text-ink-400 text-sm">Answer the five questions to see your recommended plan.</p>}
        </div>
        <div className="card-hairline p-5"><div className="eyebrow mb-2">How this decides</div><p className="font-mono text-xs text-ink-800">plan = highest requirement across channels, volume, media, platform needs and team size.</p></div>
      </div>
    </div>
  );
}

export default function ToolDetail() {
  const { slug } = useParams();
  const { data: p, isLoading } = useQuery({ queryKey: ["tool", slug], queryFn: () => get(`/content/tool/${slug}`) });
  if (isLoading) return <Spinner />;

  let widget;
  if (slug === "plan-picker") widget = <PlanPicker />;
  else if (CALC[slug]) widget = <SchemaCalculator schema={CALC[slug]} />;
  else if (NICHE[slug]) widget = <NicheCalculator cfg={NICHE[slug]} />;
  else widget = (
    <div className="card-hairline p-6" data-testid="calc-unavailable">
      <div className="eyebrow mb-2">Calculator</div>
      <p className="text-ink-600">This calculator is being prepared. In the meantime, explore the
        {" "}<Link to="/tools/" className="text-brand-700 underline">full tools library</Link>.</p>
    </div>
  );

  return (
    <>
      <Seo title={p?.title || "Calculator"} path={`/tools/${slug}/`} description={p?.description}
        jsonLd={[{ "@context": "https://schema.org", "@type": "SoftwareApplication", name: p?.title, applicationCategory: "BusinessApplication", offers: { "@type": "Offer", price: 0, priceCurrency: "PHP" } }, breadcrumbJsonLd([{ name: "Tools", path: "/tools/" }, { name: p?.title || slug, path: `/tools/${slug}/` }]), faqJsonLd(p?.faq)]} />
      <Section>
        <Breadcrumb items={[{ name: "Tools", path: "/tools/" }, { name: p?.title || slug }]} />
        <h1 className="mt-6 text-4xl md:text-5xl tracking-tight text-ink-950 max-w-3xl">{p?.title}</h1>
        <p className="mt-4 text-lg text-ink-600 max-w-prose">{p?.description}</p>
        <div className="mt-10">{widget}</div>
      </Section>
      <Section sunk>
        <div className="max-w-prose"><Markdown source={p?.body} /></div>
        {p?.faq?.length > 0 && <div className="mt-10 max-w-3xl"><h2 className="text-xl text-ink-950 mb-4">FAQ</h2><FaqAccordion items={p.faq} /></div>}
      </Section>
    </>
  );
}
